import { useState, useCallback, useMemo, useRef, useEffect, type Dispatch, type SetStateAction } from 'react'
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import apiClient, { ApiClientError } from '@/api/client'
import type { StockCategory, StockItem, StockAlert, ExpiringBatchAlert, UserBrief } from '@/types'
import { DEFAULT_EXPIRY_ALERT_DAYS, businessTodayIso, defaultExpiryFor, expiryLabel, expiryMissing, expiryState, expiryTone, parseShelfLife } from '@/lib/expiry'
import { ExpiryDateField, ExpiryTrackingFields } from '@/components/store/inventory/ExpiryFields'
import { WriteOffLotDialog, type WriteOffTarget } from '@/components/store/inventory/WriteOffLotDialog'
import { DateStockDialog, useUndatedLots } from '@/components/store/inventory/DateStockDialog'
import { useDayBoundaryHour } from '@/hooks/useDayBoundaryHour'
import { formatDateHuman } from '@/lib/utils'
import { useCurrency } from '@/contexts/CurrencyContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Package, AlertTriangle, TrendingUp, Plus, Search, ArrowDownCircle,
  Boxes, BarChart3, ShoppingCart, Tag, X, CheckCircle2, XCircle,
  MoreVertical, Trash2, DollarSign,
  ArrowUpDown, ArrowUp, ArrowDown,
  CalendarClock, ClipboardCheck, FileUp, ScanLine,
  ClipboardList, MapPin,
  Salad,
  Merge,
} from 'lucide-react'
import { InventoryPurchasingTab, type PODraft } from '@/components/store/InventoryPurchasingTab'
import { QuickPurchaseDialog } from '@/components/store/QuickPurchaseDialog'
import { RestockBoughtDialog } from '@/components/store/purchasing/RestockBoughtDialog'
import { marketPlacesFor } from '@/lib/inventoryFlow/marketLogic'
import { VendorsDirectory } from '@/components/store/VendorsDirectory'
import type { InventoryTab, InventorySearch } from '@/components/store/inventoryRouteSearch'
// Lane C — kitchen flow tabs (Requests, Locations), gated by kitchen_flow (useKitchenFlowEnabled, imported below).
import { getStoredUserRole } from '@/lib/staff-roles'
import { StoreRequestsTab, useStoreRequestsBadge } from '@/components/inventory-flow/StoreRequestsTab'
import { LocationsCard } from '@/components/inventory-flow/LocationsCard'
// Kitchen flow Stage 3 — count sessions.
import { CountSheet } from '@/components/inventory-flow/CountSheet'
import { useCountLocation } from '@/components/inventory-flow/useCountLocation'
import { CT } from '@/lib/inventoryFlow/countStrings'
import { PaginationControlsComponent } from '@/components/ui/pagination-controls'
import type { PaginationControls } from '@/hooks/usePagination'
import { UNIT_OPTIONS, type UnitMode, convertUnits, getCompatibleUnits } from '@/lib/inventory-units'
import { UnitSelector, FormField } from '@/components/store/inventory-form-ui'
import { AddStockItemModal } from '@/components/store/AddStockItemModal'
import { compactCurrency } from '@/components/admin/dashboard/dashboardFormat'
import { ReportsTab } from '@/components/store/reports/ReportsTab'
import { ItemDrawer } from '@/components/store/inventory/ItemDrawer'
import { ManageCategoriesDialog } from '@/components/store/inventory/ManageCategoriesDialog'
import { InventoryHistoryTab } from '@/components/store/inventory/InventoryHistoryTab'
import { ReorderLevelPopover } from '@/components/store/inventory/ReorderLevelPopover'
import { itemStatusMeta, contextAction } from '@/components/store/inventory/itemStatus'
import { fmtQty, fmtQtyUnit, isNearIntegerArtifact, QTY_ARTIFACT_HINT } from '@/lib/quantity'
// Lane A (kitchen flow) — bulk import, scanning, barcodes, purchase units, lots.
import { ImportItemsDialog } from '@/components/store/inventory/ImportItemsDialog'
import { MergeItemDialog } from '@/components/store/inventory/MergeItemDialog'
import { MergeAnswersDialog } from '@/components/store/inventory/MergeAnswersDialog'
import { MG } from '@/lib/inventoryFlow/mergeStrings'
import { BarcodeScanDialog } from '@/components/store/inventory/BarcodeScanDialog'
import { FefoHint, useStockItemLots } from '@/components/store/inventory/ItemMasterSections'
import { IM } from '@/components/store/inventory/itemMasterStrings'
import { useKitchenFlowEnabled } from '@/hooks/useKitchenFlowEnabled'
import { useKeyboardWedgeScanner } from '@/hooks/useKeyboardWedgeScanner'
import { purchaseUnitOf, toBaseQty } from '@/lib/purchaseUnits'
import { useItemScanner } from '@/components/store/inventory/useItemScanner'
import { WasteTab } from '@/components/inventory-flow/WasteTab'
import { WT } from '@/lib/inventoryFlow/wasteStrings'
import { BasicsTab } from '@/components/inventory-flow/BasicsTab'
import { BT } from '@/lib/inventoryFlow/basicsStrings'
import { qtyAfterScan } from '@/lib/scanToLine'
// Batch 4 (kitchen flow, lane store): grouped tabs and the quiet no-levels note.
import { NoLevelsNote, StoreTabBar } from '@/components/store/StoreTabBar'
import { defaultStoreTab, noLevelsNote, realAlerts } from '@/lib/inventoryFlow/storeScreenLogic'
import { ST } from '@/lib/inventoryFlow/storeScreenStrings'
import { exceedsShelf, storeShelf } from '@/lib/inventoryFlow/storeShelfLogic'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { CountTarget } from '@/components/store/reports/primitives'

const ISSUE_REASONS = [
  { value: 'General Kitchen Use', label: 'General Kitchen Use' },
  { value: 'Spoilage/Waste', label: 'Spoilage / Waste' },
  { value: 'Return to Vendor', label: 'Return to Vendor' },
  { value: 'Staff Use', label: 'Staff Use' },
  { value: 'Cleaning', label: 'Cleaning' },
  { value: 'Other', label: 'Other' },
] as const

const ADJUST_REASONS = [
  { value: 'Physical count correction', label: 'Physical count correction' },
  { value: 'Damage / loss', label: 'Damage / loss' },
  { value: 'System correction', label: 'System correction' },
  { value: 'Other', label: 'Other' },
] as const

type Toast = { id: number; type: 'success' | 'error'; message: string }
let toastIdCounter = 0

type Tab = InventoryTab
type ModalState =
  | { kind: 'none' }
  | { kind: 'addItem' }
  | { kind: 'editItem'; item: StockItem }
  | { kind: 'purchase'; item: StockItem }
  | { kind: 'issue'; item: StockItem }
  | { kind: 'supplierReturn'; item: StockItem }
  | { kind: 'adjust'; item: StockItem }
  | { kind: 'count'; item: StockItem }
  | { kind: 'merge'; item: StockItem }

type StockStatusFilter = '' | 'low' | 'ok' | 'expiring'

export type ItemSortKey = 'category' | 'name' | 'on_hand' | 'reorder' | 'unit_cost' | 'avg_cost' | 'expiry'

export function StoreInventoryDashboard() {
  const qc = useQueryClient()
  const { formatCurrency } = useCurrency()
  // The active tab lives in the URL (F-FE-N93), so a vendor drill-in is a
  // link and "see Vendors / AP" stops being an instruction to go navigate.
  // `strict: false` because this screen is also mounted by the legacy
  // AdminLayout shell, outside the /admin/inventory route.
  const navigate = useNavigate()
  const inventorySearch = useSearch({ strict: false }) as InventorySearch
  // Batch 4: with the kitchen flow on, the inventory manager lands on To do.
  const kitchenFlow = useKitchenFlowEnabled()
  const tab: Tab = inventorySearch.tab ?? defaultStoreTab(kitchenFlow, getStoredUserRole())
  const setTab = useCallback((next: Tab) => {
    // Keep the Reports date range across tab switches (F6): the range lives in
    // the same URL search params, and dropping it here silently reset the
    // report the user had configured.
    navigate({
      to: '/admin/inventory',
      search: (prev: Record<string, unknown>) => ({
        tab: next,
        ...(typeof prev.from === 'string' ? { from: prev.from } : {}),
        ...(typeof prev.to === 'string' ? { to: prev.to } : {}),
        ...(typeof prev.preset === 'string' ? { preset: prev.preset } : {}),
      }),
      replace: true,
    })
  }, [navigate])
  const [modal, setModal] = useState<ModalState>({ kind: 'none' })
  const [manageCategoriesOpen, setManageCategoriesOpen] = useState(false)
  // History tab's item scope, set by the drawer's "View all in History".
  const [historyItemFilter, setHistoryItemFilter] = useState<{ id: string; name: string } | null>(null)
  // The item drawer is URL state (?item=<uuid>), consistent with the
  // vendor/po drill-in convention — an item's page is a link.
  const drawerItemId = inventorySearch.item ?? null
  const openItemDrawer = useCallback((id: string) => {
    navigate({
      to: '/admin/inventory',
      search: (prev: Record<string, unknown>) => ({ ...prev, tab: 'items' as const, item: id }),
      replace: true,
    })
  }, [navigate])
  const closeItemDrawer = useCallback(() => {
    navigate({
      to: '/admin/inventory',
      search: (prev: Record<string, unknown>) => {
        const { item: _drop, ...rest } = prev
        return rest
      },
      replace: true,
    })
  }, [navigate])
  // The Reports tab (and its "count these" worklist) opens the same count
  // dialog the Items tab uses; it only knows the fields a count needs, so a
  // minimal StockItem is synthesized around them. The dialog re-validates the
  // book quantity server-side (409 on staleness), so a slightly stale on_hand
  // here is safe.
  const openCountFor = useCallback((t: CountTarget) => {
    setModal({
      kind: 'count',
      item: {
        id: t.id,
        name: t.name,
        unit: t.unit,
        quantity_on_hand: t.quantity_on_hand,
        reorder_level: 0,
        default_unit_cost: t.unit_cost,
        is_active: true,
        created_at: '',
        updated_at: '',
      } as StockItem,
    })
  }, [])
  const [search, setSearch] = useState('')

  // ── Lane A (kitchen flow): import + scan-to-find ───────────────────────────
  // Import is for every tenant (the page is already admin + inventory_manager
  // only); scanning and barcodes exist only with the flag on (kitchenFlow,
  // read above for the default tab).
  const [importOpen, setImportOpen] = useState(false)
  const [mergeAnswersOpen, setMergeAnswersOpen] = useState(false)
  const [scanOpen, setScanOpen] = useState(false)
  const [scanBusy, setScanBusy] = useState(false)
  const [newBarcode, setNewBarcode] = useState<string | null>(null)
  // showToast is declared further down; the scan handler reaches it via a ref.
  const showToastRef = useRef<((t: 'success' | 'error', m: string) => void) | null>(null)
  const handleScannedCode = useCallback(async (code: string) => {
    setScanBusy(true)
    try {
      const res = await apiClient.lookupBarcode(code)
      const match = res.data
      setScanOpen(false)
      setSearch('')
      if (!match) return
      if (!match.item.is_active) {
        showToastRef.current?.('error', IM.scan.archived(match.item.name))
        return
      }
      openItemDrawer(match.item.id)
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'barcode_not_found') {
        setScanOpen(false)
        setSearch('')
        setNewBarcode(code)
      } else {
        showToastRef.current?.('error', (err as Error).message)
      }
    } finally {
      setScanBusy(false)
    }
  }, [openItemDrawer])
  // ────────────────────────────────────────────────────────────────────────────

  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [filterCategory, setFilterCategory] = useState('')
  const [filterStatus, setFilterStatus] = useState<StockStatusFilter>('')
  const [itemPage, setItemPage] = useState(1)
  const [itemPageSize, setItemPageSize] = useState(10)
  const [toasts, setToasts] = useState<Toast[]>([])
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [itemSortKey, setItemSortKey] = useState<ItemSortKey>('category')
  const [itemSortDir, setItemSortDir] = useState<'asc' | 'desc'>('asc')

  const showToast = useCallback((type: 'success' | 'error', message: string) => {
    const id = ++toastIdCounter
    setToasts(prev => [...prev, { id, type, message }])
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000)
  }, [])
  showToastRef.current = showToast
  // Lane A: a USB barcode gun works anywhere on the Items tab while nothing
  // else (a dialog, the drawer's own barcode field) owns the keyboard.
  useKeyboardWedgeScanner((code) => void handleScannedCode(code), {
    enabled: kitchenFlow && tab === 'items' && modal.kind === 'none' && !drawerItemId && !importOpen && !scanOpen && newBarcode === null,
  })

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => window.clearTimeout(t)
  }, [search])

  const { data: categoriesRes } = useQuery({ queryKey: ['stockCategories'], queryFn: () => apiClient.getStockCategories() })
  const categories = categoriesRes?.data ?? []

  const { data: summaryRes, isError: summaryError } = useQuery({
    queryKey: ['stockSummary'],
    queryFn: () => apiClient.getStockSummary('month'),
    staleTime: 30_000,
  })
  const stockOverview = summaryRes?.data?.overview

  const stockHealthParam: 'low' | 'ok' | 'expiring' | undefined = filterStatus === '' ? undefined : filterStatus
  // The expiring-soon window (F-CTRL-N73). The summary carries it, so the
  // chips on this page use the same window the counts were made with.
  const expiryWindow = stockOverview?.expiry_alert_days ?? DEFAULT_EXPIRY_ALERT_DAYS

  const handleItemSort = useCallback((key: ItemSortKey) => {
    setItemPage(1)
    setItemSortKey((prevKey) => {
      if (prevKey === key) {
        setItemSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
        return prevKey
      }
      setItemSortDir('asc')
      return key
    })
  }, [])

  const { data: itemsRes, isLoading: itemsLoading } = useQuery({
    queryKey: ['stockItems', itemPage, itemPageSize, filterCategory, debouncedSearch, filterStatus, itemSortKey, itemSortDir],
    queryFn: () =>
      apiClient.getStockItems({
        page: itemPage,
        per_page: itemPageSize,
        category_id: filterCategory || undefined,
        search: debouncedSearch || undefined,
        stock_health: stockHealthParam,
        sort: itemSortKey,
        sort_dir: itemSortDir,
      }),
    placeholderData: keepPreviousData,
  })
  const items = itemsRes?.data ?? []
  const itemsMeta = itemsRes?.meta
  const itemsTotal = itemsMeta?.total ?? 0

  const itemsPagination = useMemo((): PaginationControls => {
    const totalPages = Math.max(1, Math.ceil(itemsTotal / itemPageSize))
    return {
      page: itemPage,
      pageSize: itemPageSize,
      totalPages,
      hasNextPage: itemPage < totalPages,
      hasPreviousPage: itemPage > 1,
      goToPage: (p: number) => setItemPage(Math.max(1, Math.min(p, totalPages))),
      goToNextPage: () => setItemPage((p) => Math.min(p + 1, totalPages)),
      goToPreviousPage: () => setItemPage((p) => Math.max(1, p - 1)),
      goToFirstPage: () => setItemPage(1),
      goToLastPage: () => setItemPage(totalPages),
      setPageSize: (s: number) => {
        setItemPageSize(s)
        setItemPage(1)
      },
    }
  }, [itemPage, itemPageSize, itemsTotal])

  const { data: alertsRes } = useQuery({ queryKey: ['stockAlerts'], queryFn: () => apiClient.getStockAlerts() })
  const allAlerts: StockAlert[] = alertsRes?.data ?? []
  // Batch 4: with the kitchen flow on, an item with no reorder level and
  // nothing on hand is not a warning — it is one quiet "set levels" note. The
  // backend's alerts are unchanged; only this screen's flag-on view filters.
  const alerts: StockAlert[] = useMemo(() => (kitchenFlow ? realAlerts(allAlerts) : allAlerts), [kitchenFlow, allAlerts])

  const { data: expiringRes } = useQuery({
    queryKey: ['expiringBatches'],
    queryFn: () => apiClient.getExpiringStockBatches(),
    // Always loaded: the Alerts badge counts these, and a badge that only
    // appears once the tab is open tells nobody anything.
    staleTime: 30_000,
  })
  const expiringBatches: ExpiringBatchAlert[] = expiringRes?.data ?? []

  // Alerts → Purchasing handoff: "Create PO for <vendor>" builds a draft here,
  // switches tabs, and InventoryPurchasingTab opens its dialog pre-filled.
  const [poDraft, setPoDraft] = useState<PODraft | null>(null)

  const { data: catalogPickRes } = useQuery({
    queryKey: ['stockItems', 'catalog-pick'],
    // Pages rather than asking for 500 in one call — the endpoint caps at 100
    // and used to answer an over-cap request with its default of 20 (F-BE-N60).
    queryFn: () => apiClient.getAllStockItems({ sort: 'name', sort_dir: 'asc' }),
    enabled: tab === 'purchasing',
  })
  const catalogPickItems: StockItem[] = catalogPickRes ?? []

  const { data: usersRes } = useQuery({ queryKey: ['storeUsers'], queryFn: () => apiClient.getStoreUsers() })
  const users: UserBrief[] = usersRes?.data ?? []


  // D-P3: surface query failures instead of silently rendering "—" KPIs / an
  // empty report. A load failure now toasts so staff know the numbers are
  // stale, not zero.
  useEffect(() => {
    if (summaryError) showToast('error', 'Could not load the inventory summary — check your connection and refresh.')
  }, [summaryError, showToast])

  // Bulk selection must never outlive the view it was made in — acting on
  // rows that are no longer visible is how the old bulk delete removed items
  // the confirm dialog never listed.
  useEffect(() => {
    setSelectedIds(new Set())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemPage, debouncedSearch, filterCategory, filterStatus, itemSortKey, itemSortDir])

  // #6: sale-time stock deduction failures (dead-letter). Surfaced as a banner so
  // a manager reconciles the drift instead of it vanishing into a log line.
  const { data: deductionFailuresRes } = useQuery({
    queryKey: ['stockDeductionFailures'],
    queryFn: () => apiClient.getStockDeductionFailures(),
    refetchInterval: 60_000,
  })
  const deductionFailures = deductionFailuresRes?.data ?? []
  const resolveFailure = useMutation({
    mutationFn: (id: string) => apiClient.resolveStockDeductionFailure(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stockDeductionFailures'] })
      showToast('success', 'Marked as reconciled')
    },
    onError: (e: Error) => showToast('error', e.message || 'Failed to dismiss'),
  })

  // Lane C — the Requests / Locations tabs use lane A's kitchenFlow above.
  const isAdminUser = getStoredUserRole() === 'admin'
  const requestsBadge = useStoreRequestsBadge(kitchenFlow)
  const noLevels = kitchenFlow
    ? noLevelsNote({ noLevelCount: stockOverview?.no_level_count, totalItems: stockOverview?.total_items, quietAlerts: allAlerts.length - alerts.length })
    : null
  const tabs: { id: Tab; label: string; icon: React.ReactNode; badge?: number }[] = [
    { id: 'items', label: 'Inventory', icon: <Package className="w-4 h-4" /> },
    { id: 'purchasing', label: 'Purchasing', icon: <ShoppingCart className="w-4 h-4" /> },
    { id: 'vendors', label: 'Vendors', icon: <DollarSign className="w-4 h-4" /> },
    { id: 'alerts', label: 'Alerts', icon: <AlertTriangle className="w-4 h-4" />, badge: alerts.length + expiringBatches.length },
    { id: 'history', label: 'History', icon: <Boxes className="w-4 h-4" /> },
    { id: 'reports', label: 'Reports', icon: <BarChart3 className="w-4 h-4" /> },
    // Lane C — kitchen flow. Absent unless kitchen_flow is on.
    ...(kitchenFlow ? [{ id: 'requests' as Tab, label: 'Requests', icon: <ClipboardList className="w-4 h-4" />, badge: requestsBadge }] : []),
    ...(kitchenFlow && isAdminUser ? [{ id: 'locations' as Tab, label: 'Locations', icon: <MapPin className="w-4 h-4" /> }] : []),
    // Kitchen flow Stage 3 — count one place at a time.
    ...(kitchenFlow ? [{ id: 'count' as Tab, label: CT.tab, icon: <ClipboardCheck className="w-4 h-4" /> }] : []),
    // Stage 2C: the waste log.
    ...(kitchenFlow ? [{ id: 'waste' as Tab, label: WT.storeTab, icon: <Trash2 className="w-4 h-4" /> }] : []),
    // Stage 2D: kitchen basics running low — push a send.
    ...(kitchenFlow ? [{ id: 'basics' as Tab, label: BT.storeTab, icon: <Salad className="w-4 h-4" /> }] : []),
  ]

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Store Inventory</h1>
          <p className="text-muted-foreground">Manage supplies, purchases, and issues</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setModal({ kind: 'addItem' })} size="sm">
            <Plus className="w-4 h-4 mr-1" /> Add Item
          </Button>
          <Button onClick={() => setManageCategoriesOpen(true)} variant="outline" size="sm">
            <Tag className="w-4 h-4 mr-1" /> Manage categories
          </Button>
          <Button onClick={() => setImportOpen(true)} variant="outline" size="sm">
            <FileUp className="w-4 h-4 mr-1" /> {IM.import.button}
          </Button>
          {kitchenFlow && (
            <Button onClick={() => setScanOpen(true)} variant="outline" size="sm">
              <ScanLine className="w-4 h-4 mr-1" /> {IM.scan.button}
            </Button>
          )}
          {kitchenFlow && isAdminUser && (
            <Button onClick={() => setMergeAnswersOpen(true)} variant="outline" size="sm">
              <Merge className="w-4 h-4 mr-1" /> {MG.bulk.button}
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-info-surface shrink-0"><Package className="w-5 h-5 text-info-fg" /></div>
              <div className="min-w-0">
                <p className="truncate text-xl font-bold sm:text-2xl">
                  {stockOverview?.total_items != null ? stockOverview.total_items : (itemsMeta?.total ?? '—')}
                </p>
                <p className="text-xs text-muted-foreground">Total Items</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-danger-surface shrink-0"><AlertTriangle className="w-5 h-5 text-red-600" /></div>
              <div className="min-w-0">
                <p className="truncate text-xl font-bold sm:text-2xl">
                  {kitchenFlow ? alerts.length : stockOverview?.low_stock_count != null ? stockOverview.low_stock_count : alerts.length}
                </p>
                <p className="text-xs text-muted-foreground">Low Stock</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-success-surface shrink-0"><Tag className="w-5 h-5 text-green-600" /></div>
              <div className="min-w-0">
                <p className="truncate text-xl font-bold sm:text-2xl">{categories.length}</p>
                <p className="text-xs text-muted-foreground">Categories</p>
              </div>
            </div>
          </CardContent>
        </Card>
        {/* F-CTRL-N73: expiring and expired lots; opens the Alerts tab. */}
        <Card
          role="button"
          tabIndex={0}
          className="cursor-pointer transition-colors hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => setTab('alerts')}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setTab('alerts') } }}
          title={stockOverview?.expiry_value_at_risk ? `${formatCurrency(stockOverview.expiry_value_at_risk)} of stock at risk` : undefined}
        >
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg shrink-0 ${(stockOverview?.expired_lot_count ?? 0) > 0 ? 'bg-danger-surface' : 'bg-warning-surface'}`}>
                <CalendarClock className={`w-5 h-5 ${(stockOverview?.expired_lot_count ?? 0) > 0 ? 'text-danger-fg' : 'text-warning-fg'}`} />
              </div>
              <div className="min-w-0">
                <p className="truncate text-xl font-bold sm:text-2xl">
                  {stockOverview ? (stockOverview.expiring_lot_count ?? 0) + (stockOverview.expired_lot_count ?? 0) : '—'}
                </p>
                <p className="truncate text-xs text-muted-foreground">
                  {(stockOverview?.expired_lot_count ?? 0) > 0
                    ? `Expiring · ${stockOverview!.expired_lot_count} expired`
                    : `Expiring in ${expiryWindow} days`}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-secondary shrink-0"><TrendingUp className="w-5 h-5 text-purple-600" /></div>
              <div className="min-w-0">
                <p
                  className="truncate text-base font-bold sm:text-2xl"
                  title={stockOverview?.total_value != null ? formatCurrency(stockOverview.total_value) : undefined}
                >
                  {stockOverview?.total_value != null ? compactCurrency(stockOverview.total_value) : '—'}
                </p>
                <p className="text-xs text-muted-foreground">Stock Value</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {deductionFailures.length > 0 && (
        <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm dark:border-red-900 dark:bg-red-950/30">
          <div className="font-medium text-red-800 dark:text-red-200">
            {deductionFailures.length} stock deduction{deductionFailures.length === 1 ? '' : 's'} failed at sale time — that stock
            wasn&apos;t decremented. Adjust the affected item(s) manually, then dismiss.
          </div>
          <ul className="mt-2 space-y-1">
            {deductionFailures.slice(0, 5).map(f => (
              <li key={f.id} className="flex items-center justify-between gap-3 text-xs text-red-900/80 dark:text-red-100/80">
                <span>
                  {f.product_name ?? 'Item'}{f.order_number ? ` · #${f.order_number}` : ''} — {f.reason ?? 'failed'}
                </span>
                <button
                  type="button"
                  className="shrink-0 rounded border border-red-300 px-2 py-0.5 hover:bg-red-100 disabled:opacity-50 dark:border-red-800 dark:hover:bg-red-900/40"
                  disabled={resolveFailure.isPending}
                  onClick={() => resolveFailure.mutate(f.id)}
                >
                  Dismiss
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {kitchenFlow && noLevels != null && (
        <NoLevelsNote count={noLevels} onSetLevels={() => {
          // Levels are set per item; sorting by reorder level puts the
          // unset ones first.
          setItemSortKey('reorder')
          setItemSortDir('asc')
          setItemPage(1)
          setTab('items')
        }} />
      )}

      {kitchenFlow ? (
        <StoreTabBar
          tab={tab}
          onTab={(t) => { setTab(t); setItemPage(1) }}
          isAdmin={isAdminUser}
          todoBadge={requestsBadge}
          moreLabels={{ alerts: ST.more.alerts, history: ST.more.history, reports: ST.more.reports, waste: WT.storeTab, basics: BT.storeTab, locations: ST.more.locations }}
          moreBadges={{ alerts: alerts.length + expiringBatches.length }}
        />
      ) : (
      <div className="no-scrollbar flex max-w-full gap-1 overflow-x-auto border-b pb-0 [&>*]:shrink-0">
        {tabs.map(t => (
          <button key={t.id} onClick={() => { setTab(t.id); setItemPage(1) }}
            className={`flex items-center gap-2 whitespace-nowrap px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === t.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {t.icon} {t.label}
            {t.badge != null && t.badge > 0 && <Badge variant="destructive" className="ml-1 text-xs px-1.5 py-0">{t.badge}</Badge>}
          </button>
        ))}
      </div>
      )}

      {tab === 'items' && <ItemsTab items={items} categories={categories} loading={itemsLoading}
        search={search} setSearch={setSearch} filterCategory={filterCategory} setFilterCategory={setFilterCategory}
        filterStatus={filterStatus} setFilterStatus={setFilterStatus}
        pagination={itemsPagination} total={itemsTotal} setModal={setModal} qc={qc} showToast={showToast}
        selectedIds={selectedIds} setSelectedIds={setSelectedIds}
        itemSortKey={itemSortKey} itemSortDir={itemSortDir} onItemSort={handleItemSort}
        onOpenDrawer={openItemDrawer} expiryWindow={expiryWindow} />}
      {tab === 'purchasing' && <InventoryPurchasingTab stockItems={catalogPickItems} alerts={alerts} showToast={showToast} initialDraft={poDraft} onDraftConsumed={() => setPoDraft(null)} />}
      {tab === 'vendors' && <VendorsDirectory showToast={showToast} />}

      {tab === 'alerts' && (
        <AlertsTab
          alerts={alerts}
          expiring={expiringBatches}
          expiryWindow={expiryWindow}
          setModal={setModal}
          showToast={showToast}
          onCreatePO={(draft) => { setPoDraft(draft); setTab('purchasing') }}
        />
      )}
      {tab === 'history' && (
        <InventoryHistoryTab
          categories={categories}
          showToast={showToast}
          itemFilter={historyItemFilter}
          onClearItemFilter={() => setHistoryItemFilter(null)}
        />
      )}
      {tab === 'reports' && <ReportsTab onCountItem={openCountFor} onOpenAlerts={() => setTab('alerts')} />}
      {/* Lane C — kitchen flow */}
      {tab === 'requests' && kitchenFlow && <StoreRequestsTab showToast={showToast} onThrowAway={() => setTab('alerts')} />}
      {tab === 'waste' && kitchenFlow && <WasteTab notify={showToast} />}
      {tab === 'basics' && kitchenFlow && <BasicsTab notify={showToast} side="store" />}
      {tab === 'locations' && kitchenFlow && isAdminUser && <LocationsCard showToast={showToast} />}
      {/* Kitchen flow Stage 3 */}
      {tab === 'count' && kitchenFlow && <CountSheet mode="store" notify={showToast} />}

      {/* Add-item now uses the shared AddStockItemModal (also used by the
          product form). It renders its own overlay, so it lives outside the
          ModalOverlay switch below. */}
      {drawerItemId && (
        <ItemDrawer
          itemId={drawerItemId}
          onClose={closeItemDrawer}
          showToast={showToast}
          onAction={(action, item) => setModal({ kind: action, item })}
          onViewFullHistory={(id, name) => {
            setHistoryItemFilter({ id, name })
            closeItemDrawer()
            setTab('history')
          }}
        />
      )}

      <ManageCategoriesDialog open={manageCategoriesOpen} onClose={() => setManageCategoriesOpen(false)} showToast={showToast} />

      <AddStockItemModal open={modal.kind === 'addItem'} variant="store" onClose={() => setModal({ kind: 'none' })} />

      {/* Lane A (kitchen flow): import for every tenant; scan + "create item
          with this barcode" only with the flag on. */}
      <ImportItemsDialog open={importOpen} onClose={() => setImportOpen(false)} />
      {kitchenFlow && (
        <>
          {isAdminUser && (
            <>
              <MergeItemDialog item={modal.kind === 'merge' ? modal.item : null} onClose={() => setModal({ kind: 'none' })} />
              <MergeAnswersDialog open={mergeAnswersOpen} onClose={() => setMergeAnswersOpen(false)} />
            </>
          )}
          <BarcodeScanDialog open={scanOpen} busy={scanBusy} onClose={() => setScanOpen(false)} onCode={(code) => void handleScannedCode(code)} />
          <AddStockItemModal
            open={newBarcode !== null}
            variant="store"
            defaultBarcode={newBarcode ?? undefined}
            onClose={() => setNewBarcode(null)}
            onCreated={(created) => openItemDrawer(created.id)}
          />
        </>
      )}

      {/* Quick purchase replaces the old PurchaseForm: stock arrival + the
          vendor payable in one step (the old form skipped AP entirely). It's a
          shared Dialog, so it lives outside the ModalOverlay switch. */}
      {/* Kitchen flow pilot (Batch 5): with the flag on, every Restock /
          Record a purchase goes through "Already bought" (till / petty cash /
          buyer / company, store or kitchen, bill photos). Flag off, or a role
          that can't record one: Quick purchase, as before. */}
      {kitchenFlow && marketPlacesFor(getStoredUserRole()).length > 0 ? (
        <RestockBoughtDialog
          item={modal.kind === 'purchase' ? modal.item : null}
          onClose={() => setModal({ kind: 'none' })}
          showToast={showToast}
        />
      ) : (
        <QuickPurchaseDialog
          item={modal.kind === 'purchase' ? modal.item : null}
          onClose={() => setModal({ kind: 'none' })}
          showToast={showToast}
        />
      )}

      {modal.kind !== 'none' && modal.kind !== 'addItem' && modal.kind !== 'purchase' && modal.kind !== 'merge' && <ModalOverlay onClose={() => setModal({ kind: 'none' })}>
        {modal.kind === 'editItem' && <EditItemForm item={modal.item} categories={categories} onClose={() => setModal({ kind: 'none' })} qc={qc} showToast={showToast} />}
        {modal.kind === 'issue' && <IssueForm item={modal.item} users={users} onClose={() => setModal({ kind: 'none' })} qc={qc} showToast={showToast} />}
        {modal.kind === 'supplierReturn' && <ReturnToSupplierForm item={modal.item} onClose={() => setModal({ kind: 'none' })} qc={qc} showToast={showToast} />}
        {modal.kind === 'adjust' && <AdjustForm item={modal.item} onClose={() => setModal({ kind: 'none' })} qc={qc} showToast={showToast} onSwitchToCount={() => setModal({ kind: 'count', item: (modal as { kind: 'adjust'; item: StockItem }).item })} />}
        {modal.kind === 'count' && <RecordCountForm item={modal.item} onClose={() => setModal({ kind: 'none' })} qc={qc} showToast={showToast} />}
      </ModalOverlay>}

      <div className="fixed bottom-4 right-4 z-[60] space-y-2">
        {toasts.map(t => (
          <div key={t.id} className={`flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm font-medium animate-in slide-in-from-right ${t.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>
            {t.type === 'success' ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <XCircle className="w-4 h-4 shrink-0" />}
            {t.message}
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Meatball / 3-dot menu ───────────────────────────────────────

// ─── Items Tab (redesigned) ──────────────────────────────────────

type ItemsTabProps = {
  items: StockItem[]
  categories: StockCategory[]
  loading: boolean
  search: string
  setSearch: (s: string) => void
  filterCategory: string
  setFilterCategory: (s: string) => void
  filterStatus: StockStatusFilter
  setFilterStatus: (s: StockStatusFilter) => void
  pagination: PaginationControls
  total: number
  setModal: (m: ModalState) => void
  qc: ReturnType<typeof useQueryClient>
  showToast: (type: 'success' | 'error', message: string) => void
  selectedIds: Set<string>
  setSelectedIds: Dispatch<SetStateAction<Set<string>>>
  itemSortKey: ItemSortKey
  itemSortDir: 'asc' | 'desc'
  onItemSort: (key: ItemSortKey) => void
  onOpenDrawer: (itemId: string) => void
  expiryWindow: number
}

// The sortable columns, fixed order. Last Paid and the category column moved
// into the item drawer; status carries what LOW used to fake. Next expiry is
// back (F-CTRL-N73): it left because it read "—" on every row, which stops
// being true once tracked items cannot be received without a date.
const ITEM_COLUMNS: { key: ItemSortKey | null; label: string; align: 'left' | 'right' }[] = [
  { key: 'name', label: 'Item', align: 'left' },
  { key: null, label: 'Status', align: 'left' },
  { key: 'expiry', label: 'Next expiry', align: 'left' },
  { key: 'on_hand', label: 'On hand', align: 'right' },
  { key: 'reorder', label: 'Warn at', align: 'right' },
  { key: 'avg_cost', label: 'Avg cost', align: 'right' },
]

function ItemsTab({
  items,
  categories,
  loading,
  search,
  setSearch,
  filterCategory,
  setFilterCategory,
  filterStatus,
  setFilterStatus,
  pagination,
  total,
  setModal,
  qc,
  showToast,
  selectedIds,
  setSelectedIds,
  itemSortKey,
  itemSortDir,
  onItemSort,
  onOpenDrawer,
  expiryWindow,
}: ItemsTabProps) {
  const { formatCurrency } = useCurrency()
  // Go-live: "Merge into…" — admin only, flag on.
  const kitchenFlowOn = useKitchenFlowEnabled()
  const canMerge = kitchenFlowOn && getStoredUserRole() === 'admin'
  // Batch 4: plain words for the store with the kitchen flow on.
  const restockLabel = kitchenFlowOn ? ST.recordPurchase : 'Restock'
  const [deleteItemOpen, setDeleteItemOpen] = useState(false)
  const [pendingDeleteItem, setPendingDeleteItem] = useState<StockItem | null>(null)
  const [bulkBusy, setBulkBusy] = useState(false)
  const [bulkArchiveOpen, setBulkArchiveOpen] = useState(false)
  const [bulkCategory, setBulkCategory] = useState('')
  const [bulkReorder, setBulkReorder] = useState('')
  // Names captured at selection time, so every confirm/toast can name exactly
  // what will be touched — never rows the operator can no longer see.
  const selectedNames = useRef(new Map<string, string>())

  function sortIcon(col: ItemSortKey) {
    if (itemSortKey !== col) return <ArrowUpDown className="w-4 h-4 shrink-0 opacity-40" aria-hidden />
    return itemSortDir === 'asc' ? (
      <ArrowUp className="w-4 h-4 shrink-0" aria-hidden />
    ) : (
      <ArrowDown className="w-4 h-4 shrink-0" aria-hidden />
    )
  }

  const invalidateItems = () => {
    qc.invalidateQueries({ queryKey: ['stockItems'] })
    qc.invalidateQueries({ queryKey: ['stockCategories'] })
    qc.invalidateQueries({ queryKey: ['stockSummary'] })
    qc.invalidateQueries({ queryKey: ['stockAlerts'] })
    qc.invalidateQueries({ queryKey: ['advancedStockReport'] })
  }

  const deleteMut = useMutation({
    mutationFn: (id: string) => apiClient.deleteStockItem(id),
    onSuccess: (res) => {
      invalidateItems()
      showToast('success', res?.message || 'Item deleted')
    },
    onError: (err: Error) => showToast('error', err.message || 'Failed to delete item'),
  })

  const allChecked = items.length > 0 && items.every((i: StockItem) => selectedIds.has(i.id))
  const someChecked = selectedIds.size > 0
  const headerCheckboxRef = useRef<HTMLInputElement | null>(null)
  useEffect(() => {
    if (headerCheckboxRef.current) headerCheckboxRef.current.indeterminate = someChecked && !allChecked
  }, [someChecked, allChecked])

  function toggleAll() {
    if (allChecked) {
      setSelectedIds(new Set())
      selectedNames.current.clear()
    } else {
      items.forEach((i: StockItem) => selectedNames.current.set(i.id, i.name))
      setSelectedIds(new Set(items.map((i: StockItem) => i.id)))
    }
  }

  function toggleOne(item: StockItem) {
    setSelectedIds((prev: Set<string>) => {
      const next = new Set(prev)
      if (next.has(item.id)) {
        next.delete(item.id)
        selectedNames.current.delete(item.id)
      } else {
        next.add(item.id)
        selectedNames.current.set(item.id, item.name)
      }
      return next
    })
  }

  const selectedNameList = [...selectedIds].map(id => selectedNames.current.get(id) ?? id)

  // Bulk ops = N item updates; the backend has no batch endpoint and these are
  // small counts (a page at most, selection clears on page change).
  async function runBulk(label: string, fn: (id: string) => Promise<unknown>) {
    const ids = [...selectedIds]
    setBulkBusy(true)
    try {
      const results = await Promise.allSettled(ids.map(fn))
      const fails = results.filter(r => r.status === 'rejected').length
      invalidateItems()
      setSelectedIds(new Set())
      selectedNames.current.clear()
      if (fails === 0) showToast('success', `${label} for ${ids.length} item${ids.length === 1 ? '' : 's'}.`)
      else showToast('error', `${label}: ${ids.length - fails} succeeded, ${fails} failed.`)
    } finally {
      setBulkBusy(false)
    }
  }

  function handleDeleteItem(item: StockItem) {
    setPendingDeleteItem(item)
    setDeleteItemOpen(true)
  }

  function confirmDeleteItem() {
    if (pendingDeleteItem) deleteMut.mutate(pendingDeleteItem.id)
    setDeleteItemOpen(false)
    setPendingDeleteItem(null)
  }

  return (
    <div className="space-y-4">
      {/* Search + filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          {/* F-FE-N121: the search term survives tab switches on purpose (it is
              the Items filter), so a returning tap must not append to it —
              select the whole term on focus and typing replaces it. */}
          <input value={search} onChange={e => { setSearch(e.target.value); pagination.goToFirstPage() }} placeholder="Search items, categories, codes…"
            onFocus={e => e.currentTarget.select()}
            className="w-full h-10 pl-10 pr-3 border rounded-md text-sm bg-background" />
        </div>
        <select value={filterCategory} onChange={e => { setFilterCategory(e.target.value); pagination.goToFirstPage() }}
          aria-label="Filter by category"
          className="border rounded-md px-3 h-10 text-sm bg-background min-w-[160px]">
          <option value="">All Categories</option>
          {categories.map((c: StockCategory) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value as StockStatusFilter); pagination.goToFirstPage() }}
          aria-label="Filter by status"
          className="border rounded-md px-3 h-10 text-sm bg-background min-w-[160px]">
          <option value="">All Status</option>
          <option value="low">Needs attention</option>
          <option value="expiring">Expiring or expired</option>
          <option value="ok">OK</option>
        </select>
      </div>

      {/* Bulk actions */}
      {someChecked && (
        <div className="flex flex-wrap items-center gap-3 gap-y-2 bg-primary/5 border border-primary/20 rounded-lg px-4 py-2.5">
          <span className="text-sm font-medium">{selectedIds.size} item{selectedIds.size > 1 ? 's' : ''} selected</span>
          <div className="flex-1" />
          <div className="flex items-center gap-1.5">
            <select value={bulkCategory} onChange={e => setBulkCategory(e.target.value)}
              aria-label="Bulk set category"
              className="border rounded-md px-2 h-9 text-sm bg-background">
              <option value="">Set category…</option>
              {categories.map((c: StockCategory) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <Button size="sm" variant="outline" className="h-9 text-sm" disabled={!bulkCategory || bulkBusy}
              onClick={() => void runBulk('Category set', (id) => apiClient.updateStockItem(id, { category_id: bulkCategory })).then(() => setBulkCategory(''))}>
              Apply
            </Button>
          </div>
          <div className="flex items-center gap-1.5">
            <Input type="number" min="0" step="any" placeholder="Warn at…" value={bulkReorder}
              onChange={e => setBulkReorder(e.target.value)} className="h-9 w-24 text-sm" aria-label="Bulk set reorder level" />
            <Button size="sm" variant="outline" className="h-9 text-sm"
              disabled={bulkReorder === '' || Number.isNaN(+bulkReorder) || +bulkReorder < 0 || bulkBusy}
              onClick={() => void runBulk('Reorder level set', (id) => apiClient.updateStockItem(id, { reorder_level: +bulkReorder })).then(() => setBulkReorder(''))}>
              Apply
            </Button>
          </div>
          <Button size="sm" variant="destructive" className="h-9 text-sm" disabled={bulkBusy} onClick={() => setBulkArchiveOpen(true)}>
            <Trash2 className="w-4 h-4 mr-1" /> Archive…
          </Button>
          <Button size="sm" variant="ghost" className="h-9 text-sm" onClick={() => { setSelectedIds(new Set()); selectedNames.current.clear() }}>
            Clear
          </Button>
        </div>
      )}

      <Dialog open={bulkArchiveOpen} onOpenChange={(o) => !bulkBusy && setBulkArchiveOpen(o)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Archive {selectedIds.size} item{selectedIds.size === 1 ? '' : 's'}?</DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-3 text-sm text-muted-foreground">
                <p>Archived items disappear from this list but keep their history for reports. You can permanently delete an item from its own menu if it has no history.</p>
                <ul className="list-disc pl-4 space-y-1 text-foreground">
                  {selectedNameList.slice(0, 8).map(name => <li key={name}>{name}</li>)}
                </ul>
                {selectedNameList.length > 8 && <p>and {selectedNameList.length - 8} more…</p>}
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setBulkArchiveOpen(false)} disabled={bulkBusy}>Cancel</Button>
            <Button variant="destructive" disabled={bulkBusy}
              onClick={() => { setBulkArchiveOpen(false); void runBulk('Archived', (id) => apiClient.updateStockItem(id, { is_active: false })) }}>
              {bulkBusy ? 'Archiving…' : 'Archive'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Table */}
      <div className="border rounded-lg overflow-x-auto bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 border-b">
            <tr>
              <th scope="col" className="w-12 px-3 py-3 align-middle">
                <input ref={headerCheckboxRef} type="checkbox" checked={allChecked} onChange={toggleAll}
                  className="h-4 w-4 rounded border-input cursor-pointer" aria-label="Select all rows" />
              </th>
              {ITEM_COLUMNS.map(col => (
                <th
                  key={col.label}
                  scope="col"
                  aria-sort={col.key && itemSortKey === col.key ? (itemSortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                  className={`px-4 py-3 align-middle text-sm font-semibold tracking-tight ${col.align === 'right' ? 'text-right' : 'text-left'}`}
                >
                  {col.key ? (
                    <button type="button" className={`inline-flex items-center gap-1.5 hover:text-primary ${col.align === 'right' ? 'justify-end' : ''}`}
                      onClick={() => onItemSort(col.key!)}>
                      {col.label}
                      {sortIcon(col.key)}
                    </button>
                  ) : col.label}
                </th>
              ))}
              <th scope="col" className="text-right px-4 py-3 text-sm font-semibold tracking-tight whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading && items.length === 0 && (
              <tr><td colSpan={ITEM_COLUMNS.length + 2} className="px-4 py-10 text-center text-muted-foreground text-sm">Loading…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={ITEM_COLUMNS.length + 2} className="px-4 py-10 text-center text-muted-foreground text-sm">No items found</td></tr>
            )}
            {items.map((item: StockItem) => {
              const status = itemStatusMeta(item)
              const avg = item.wac_unit_cost ?? item.default_unit_cost
              const primary = contextAction(item)
              return (
                <tr
                  key={item.id}
                  className={`cursor-pointer hover:bg-muted/30 ${selectedIds.has(item.id) ? 'bg-primary/5' : ''}`}
                  onClick={() => onOpenDrawer(item.id)}
                >
                  <td className="w-12 px-3 py-3 align-middle" onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={selectedIds.has(item.id)} onChange={() => toggleOne(item)}
                      className="h-4 w-4 rounded border-input cursor-pointer" aria-label={`Select ${item.name}`} />
                  </td>
                  <td className="px-4 py-3 align-middle">
                    <div className="font-semibold">{item.name}</div>
                    <div className="text-sm text-muted-foreground">
                      {item.category?.name ?? 'Uncategorized'} · {item.unit}
                    </div>
                  </td>
                  <td className="px-4 py-3 align-middle">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap ${status.chipCls}`}>
                      {status.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 align-middle whitespace-nowrap">
                    <NextExpiryCell item={item} windowDays={expiryWindow} />
                  </td>
                  <td className={`px-4 py-3 align-middle text-right tabular-nums font-semibold ${status.qtyCls}`}
                    title={isNearIntegerArtifact(item.quantity_on_hand) ? QTY_ARTIFACT_HINT : undefined}>
                    {fmtQtyUnit(item.quantity_on_hand, item.unit)}
                    {(item.on_order_qty ?? 0) > 0 && (
                      <div className="text-xs font-normal text-muted-foreground">+{fmtQty(item.on_order_qty!)} on order</div>
                    )}
                  </td>
                  <td className="px-4 py-3 align-middle text-right" onClick={e => e.stopPropagation()}>
                    <ReorderLevelPopover item={item} showToast={showToast} />
                  </td>
                  <td className="px-4 py-3 align-middle text-right tabular-nums">
                    {avg != null ? formatCurrency(avg) : '—'}
                  </td>
                  <td className="px-4 py-3 align-middle whitespace-nowrap text-right" onClick={e => e.stopPropagation()}>
                    <div className="inline-flex items-center gap-1.5">
                      {primary === 'count' ? (
                        <Button size="sm" className="h-9 text-sm" onClick={() => setModal({ kind: 'count', item })}>
                          <ClipboardCheck className="w-4 h-4 mr-1" /> Count
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" className="h-9 text-sm" onClick={() => setModal({ kind: 'purchase', item })}>
                          <ArrowDownCircle className="w-4 h-4 mr-1" /> {restockLabel}
                        </Button>
                      )}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="sm" variant="ghost" className="h-9 w-9 p-0" aria-label={`More actions for ${item.name}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem onClick={() => onOpenDrawer(item.id)}>Details & history</DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => setModal({ kind: 'count', item })}>Count</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setModal({ kind: 'purchase', item })}>{restockLabel}</DropdownMenuItem>
                          <DropdownMenuItem disabled={item.quantity_on_hand <= 0} onClick={() => setModal({ kind: 'issue', item })}>
                            {item.quantity_on_hand <= 0 ? 'Issue (nothing on hand)' : 'Issue stock'}
                          </DropdownMenuItem>
                          <DropdownMenuItem disabled={item.quantity_on_hand <= 0} onClick={() => setModal({ kind: 'supplierReturn', item })}>
                            {item.quantity_on_hand <= 0 ? 'Return (nothing on hand)' : 'Return to vendor'}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setModal({ kind: 'adjust', item })}>Adjust</DropdownMenuItem>
                          {canMerge && <DropdownMenuItem onClick={() => setModal({ kind: 'merge', item })}>{MG.menu}</DropdownMenuItem>}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => setModal({ kind: 'editItem', item })}>Edit</DropdownMenuItem>
                          <DropdownMenuItem className="text-danger-fg" onClick={() => handleDeleteItem(item)}>Delete / archive</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <PaginationControlsComponent pagination={pagination} total={total} className="pt-4 flex-wrap gap-y-2" />

      <Dialog open={deleteItemOpen} onOpenChange={setDeleteItemOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete inventory item?</DialogTitle>
            <DialogDescription>
              {pendingDeleteItem
                ? `Delete "${pendingDeleteItem.name}"? If this item has past stock activity (purchases, sales, counts), it will be archived instead so your reports stay accurate. Items with stock on hand or open purchase orders cannot be removed.`
                : 'Delete this item? If it has past stock activity, it will be archived so reports stay accurate.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDeleteItemOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmDeleteItem} disabled={deleteMut.isPending}>
              {deleteMut.isPending ? 'Working…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

/** Next expiry among the item's open lots: the date, and a chip once it is
 *  inside the expiring-soon window. A tracked item holding undated stock says
 *  so, because that is stock nobody can vouch for. */
function NextExpiryCell({ item, windowDays }: { item: StockItem; windowDays: number }) {
  if (!item.earliest_expiry) {
    if (item.tracks_expiry && item.quantity_on_hand > 0) {
      return <span className="text-sm text-warning-fg">No date on stock</span>
    }
    return <span className="text-sm text-muted-foreground">—</span>
  }
  const state = expiryState(item.earliest_expiry_days, windowDays)
  return (
    <div>
      <div className="text-sm">{formatDateHuman(item.earliest_expiry)}</div>
      {(state === 'expired' || state === 'today' || state === 'soon') && (
        <span className={`mt-0.5 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${expiryTone(state).chipCls}`}>
          {expiryLabel(item.earliest_expiry_days)}
        </span>
      )}
    </div>
  )
}

// ─── Other tab components (unchanged) ────────────────────────────


// ─── Alerts tab ──────────────────────────────────────────────────

const ALERT_SEVERITY_META: Record<StockAlert['severity'], {
  label: string
  badge: 'danger' | 'warning-surface'
  card: string
  bar: string
}> = {
  oversold: { label: 'Oversold', badge: 'danger', card: 'border-danger-border bg-danger-surface/40', bar: 'bg-danger-solid' },
  out: { label: 'Out of stock', badge: 'danger', card: 'border-danger-border bg-danger-surface/30', bar: 'bg-danger-solid' },
  low: { label: 'Low', badge: 'warning-surface', card: 'border-warning-border bg-warning-surface/30', bar: 'bg-warning-solid' },
}

const daysAgo = (iso: string) => {
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000)
  return d <= 0 ? 'today' : d === 1 ? 'yesterday' : `${d}d ago`
}

// PurchaseForm wants a StockItem; the enriched alert carries everything it
// actually reads, so the Restock button no longer depends on a second query.
const alertToStockItem = (a: StockAlert): StockItem => ({
  id: a.id,
  category_id: a.category_id,
  name: a.name,
  unit: a.unit,
  quantity_on_hand: a.quantity_on_hand,
  reorder_level: a.reorder_level,
  default_unit_cost: a.default_unit_cost,
  supplier_id: a.supplier_id ?? null,
  supplier_name: a.supplier_name,
  is_active: true,
  created_at: '',
  updated_at: '',
})

const alertLineCost = (a: StockAlert): number | undefined =>
  a.last_purchase_cost ?? a.wac_unit_cost ?? (a.default_unit_cost > 0 ? a.default_unit_cost : undefined)

function AlertRow({ alert: a, setModal, showToast, formatCurrency }: {
  alert: StockAlert
  setModal: (m: ModalState) => void
  showToast: (t: 'success' | 'error', m: string) => void
  formatCurrency: (n: number) => string
}) {
  const meta = ALERT_SEVERITY_META[a.severity]
  const covered = a.on_order_qty > 0
  const depletion = a.reorder_level > 0 ? Math.max(0, Math.min(1, a.quantity_on_hand / a.reorder_level)) : 0
  return (
    <Card className={covered ? 'border-border bg-muted/20' : meta.card}>
      <CardContent className="py-3 px-4 flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={meta.badge} className="shrink-0">{meta.label}</Badge>
            <p className="font-medium truncate">
              {a.name}
              {a.code && <span className="ml-1.5 text-xs font-normal text-muted-foreground">{a.code}</span>}
            </p>
            {covered && (
              <Badge variant="info-surface" className="shrink-0">
                {fmtQty(a.on_order_qty)} on order{a.next_expected_date ? ` · exp. ${a.next_expected_date}` : ''}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="h-1.5 w-28 shrink-0 overflow-hidden rounded-full bg-muted">
              <div className={`h-full rounded-full ${meta.bar}`} style={{ width: `${Math.max(depletion * 100, a.quantity_on_hand > 0 ? 4 : 0)}%` }} />
            </div>
            <p className="text-xs text-muted-foreground truncate">
              <span className={a.quantity_on_hand < 0 ? 'font-semibold text-danger-fg' : undefined}>
                {fmtQty(a.quantity_on_hand)} {a.unit}
              </span>
              {' '}on hand · reorder at {fmtQty(a.reorder_level)}
              {a.avg_daily_usage > 0 && <> · ~{fmtQty(Math.round(a.avg_daily_usage * 100) / 100)}/day</>}
              {a.days_cover != null && a.quantity_on_hand > 0 && (
                <> · <span className={a.days_cover < 3 ? 'font-semibold text-danger-fg' : undefined}>{a.days_cover < 1 ? '<1 day' : `~${Math.round(a.days_cover)} days`} cover</span></>
              )}
            </p>
          </div>
          <p className="text-xs text-muted-foreground">
            {a.category_name || 'Uncategorised'}
            {a.last_purchase_at && (
              <> · last purchased {daysAgo(a.last_purchase_at)}{a.last_purchase_cost != null ? ` @ ${formatCurrency(a.last_purchase_cost)}` : ''}</>
            )}
            {a.suggested_qty > 0 && <> · suggest ordering <span className="font-medium text-foreground">{fmtQty(Math.ceil(a.suggested_qty))} {a.unit}</span></>}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <ReorderLevelPopover item={a} showToast={showToast} triggerLabel="Reorder lvl" />
          <Button size="sm" variant="outline" className="text-xs h-7 shrink-0" onClick={() => setModal({ kind: 'purchase', item: alertToStockItem(a) })}>
            <ShoppingCart className="w-3 h-3 mr-1" /> Restock
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

const EXPIRY_WINDOW_CHOICES = [3, 7, 14, 30]

function AlertsTab({ alerts, expiring, expiryWindow, setModal, showToast, onCreatePO }: {
  alerts: StockAlert[]
  expiring: ExpiringBatchAlert[]
  /** The expiring-soon window, in days (the tenant's setting). */
  expiryWindow: number
  setModal: (m: ModalState) => void
  showToast: (t: 'success' | 'error', m: string) => void
  onCreatePO: (draft: PODraft) => void
}) {
  const { formatCurrency } = useCurrency()
  const qc = useQueryClient()
  const [writeOff, setWriteOff] = useState<WriteOffTarget | null>(null)
  // One window for everyone: the list, the badge, the tile, the Items filter
  // and the owner's dashboard all read this setting.
  const setWindow = useMutation({
    mutationFn: (days: number) => apiClient.updateExpirySettings(days),
    onSuccess: (_res, days) => {
      qc.invalidateQueries({ queryKey: ['stockSummary'] })
      qc.invalidateQueries({ queryKey: ['expiringBatches'] })
      qc.invalidateQueries({ queryKey: ['stockItems'] })
      showToast('success', `Now warning ${days} days before stock expires`)
    },
    onError: (e: Error) => showToast('error', e.message || 'Could not change the warning window'),
  })
  const expiredCount = expiring.filter(b => b.days_until_expiry < 0).length
  // Stock of tracked items that came in with no date: invisible to every
  // expiry alert until it gets one (F-CTRL-N73).
  const { data: undated = [] } = useUndatedLots()
  const [dateStockOpen, setDateStockOpen] = useState(false)
  const undatedCard = undated.length > 0 && (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-warning-border bg-warning-surface/30 px-4 py-3">
      <div className="min-w-0">
        <p className="text-sm font-medium">
          {undated.length} lot{undated.length === 1 ? '' : 's'} of tracked items {undated.length === 1 ? 'has' : 'have'} no expiry date
        </p>
        <p className="text-xs text-muted-foreground">Until they are dated, they can&apos;t show up in these alerts.</p>
      </div>
      <Button size="sm" variant="outline" onClick={() => setDateStockOpen(true)}>
        <CalendarClock className="w-4 h-4 mr-1" /> Date your stock
      </Button>
    </div>
  )
  const dateStockDialog = <DateStockDialog open={dateStockOpen} onClose={() => setDateStockOpen(false)} showToast={showToast} />
  const windowPicker = (
    <label className="flex items-center gap-2 text-xs text-muted-foreground">
      Warn me
      <select
        aria-label="How many days before expiry to warn"
        className="h-8 rounded-md border bg-background px-2 text-sm text-foreground"
        value={expiryWindow}
        disabled={setWindow.isPending}
        onChange={(e) => setWindow.mutate(Number(e.target.value))}
      >
        {EXPIRY_WINDOW_CHOICES.map(d => <option key={d} value={d}>{d} days</option>)}
      </select>
      before expiry
    </label>
  )

  const counts = useMemo(() => ({
    oversold: alerts.filter(a => a.severity === 'oversold').length,
    out: alerts.filter(a => a.severity === 'out').length,
    low: alerts.filter(a => a.severity === 'low').length,
  }), [alerts])

  const estRestockCost = useMemo(
    () => alerts.reduce((sum, a) => sum + a.suggested_qty * (alertLineCost(a) ?? 0), 0),
    [alerts],
  )

  const vendorGroups = useMemo(() => {
    const map = new Map<string, { key: string; supplierId?: string; name: string; rows: StockAlert[] }>()
    for (const a of alerts) {
      const key = a.supplier_id ?? 'none'
      let g = map.get(key)
      if (!g) {
        g = { key, supplierId: a.supplier_id, name: a.supplier_name ?? 'No vendor assigned', rows: [] }
        map.set(key, g)
      }
      g.rows.push(a)
    }
    // Server order (worst first) determines group order; the no-vendor bucket sinks.
    return [...map.values()].sort((x, y) => (x.key === 'none' ? 1 : 0) - (y.key === 'none' ? 1 : 0))
  }, [alerts])

  const expiredValue = useMemo(() => expiring.reduce((s, b) => s + b.value_at_risk, 0), [expiring])

  const createPOForVendor = (g: { supplierId?: string; name: string; rows: StockAlert[] }) => {
    if (!g.supplierId) return
    onCreatePO({
      supplier_id: g.supplierId,
      notes: 'Restock from low-stock alerts',
      lines: g.rows.map(a => ({
        stock_item_id: a.id,
        quantity_ordered: Math.max(Math.ceil(a.suggested_qty), 1),
        unit_cost: alertLineCost(a),
      })),
    })
  }

  if (alerts.length === 0 && expiring.length === 0) return (
    <div className="space-y-6">
      {undatedCard}
      <div className="text-center py-12 text-muted-foreground">
        <CheckCircle2 className="w-10 h-10 mx-auto mb-3 text-success-fg" />
        <p className="font-medium">All stock levels are healthy</p>
        <p className="text-xs mt-1">Nothing below its reorder level, and no lots expiring in the next {expiryWindow} days.</p>
        <div className="mt-4 flex justify-center">{windowPicker}</div>
      </div>
      {dateStockDialog}
    </div>
  )

  return (
    <div className="space-y-6">
      {undatedCard}
      {/* Triage summary */}
      <div className="flex flex-wrap items-center gap-2 text-sm">
        {counts.oversold > 0 && <Badge variant="danger">{counts.oversold} oversold</Badge>}
        {counts.out > 0 && <Badge variant="danger">{counts.out} out of stock</Badge>}
        {counts.low > 0 && <Badge variant="warning-surface">{counts.low} low</Badge>}
        {expiredCount > 0 && <Badge variant="danger">{expiredCount} expired</Badge>}
        {expiring.length - expiredCount > 0 && <Badge variant="warning-surface">{expiring.length - expiredCount} expiring</Badge>}
        {estRestockCost > 0 && (
          <span className="text-xs text-muted-foreground" title={formatCurrency(estRestockCost)}>
            Est. restock cost: <span className="font-medium text-foreground">{compactCurrency(estRestockCost)}</span>
          </span>
        )}
      </div>

      {vendorGroups.map(g => (
        <div key={g.key} className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-sm font-semibold">
              {g.name} <span className="font-normal text-muted-foreground">· {g.rows.length} item{g.rows.length === 1 ? '' : 's'}</span>
            </h3>
            {g.supplierId ? (
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => createPOForVendor(g)}>
                <Plus className="w-3 h-3 mr-1" /> Create PO
              </Button>
            ) : (
              <span className="text-xs text-muted-foreground">Assign a vendor on the item to order from here</span>
            )}
          </div>
          <div className="space-y-2">
            {g.rows.map(a => (
              <AlertRow key={a.id} alert={a} setModal={setModal} showToast={showToast} formatCurrency={formatCurrency} />
            ))}
          </div>
        </div>
      ))}

      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-sm font-semibold flex items-center gap-1.5">
            <CalendarClock className="w-4 h-4" /> Expiring soon
            <span className="font-normal text-muted-foreground">· next {expiryWindow} days</span>
          </h3>
          <div className="flex flex-wrap items-center gap-4">
            {expiring.length > 0 && (
              <span className="text-xs text-muted-foreground" title={formatCurrency(expiredValue)}>
                Value at risk: <span className="font-medium text-foreground">{compactCurrency(expiredValue)}</span>
              </span>
            )}
            {windowPicker}
          </div>
        </div>
        {expiring.length === 0 && (
          <p className="rounded-lg border border-dashed bg-muted/20 p-4 text-sm text-muted-foreground">
            No dated stock expires in the next {expiryWindow} days. Only stock received with an expiry date shows here.
          </p>
        )}
        <div className="space-y-2">
          {expiring.map(b => {
            const tone = expiryTone(expiryState(b.days_until_expiry, expiryWindow))
            const expired = b.days_until_expiry < 0
            return (
              <Card key={b.batch_id} className={tone.cardCls}>
                <CardContent className="py-3 px-4 flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Badge variant={tone.badge} className="shrink-0">{expiryLabel(b.days_until_expiry)}</Badge>
                      <p className="font-medium truncate">{b.item_name}</p>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {fmtQty(b.quantity_remaining)} {b.unit} in this lot · {expired ? 'expired' : 'expires'} {formatDateHuman(b.expiry_date)}
                      {b.lot_no && <> · lot {b.lot_no}</>}
                      {b.value_at_risk > 0 && <> · {formatCurrency(b.value_at_risk)} at risk</>}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant={expired ? 'destructive' : 'outline'}
                    onClick={() => setWriteOff({
                      lotId: b.batch_id, itemName: b.item_name, unit: b.unit, quantity: b.quantity_remaining,
                      unitCost: b.unit_cost, expiryDate: b.expiry_date, lotNo: b.lot_no,
                    })}
                  >
                    <Trash2 className="w-4 h-4 mr-1" /> Write off
                  </Button>
                </CardContent>
              </Card>
            )
          })}
        </div>
      </div>
      <WriteOffLotDialog target={writeOff} onClose={() => setWriteOff(null)} showToast={showToast} />
      {dateStockDialog}
    </div>
  )
}

// ─── Shared components ───────────────────────────────────────────

function ModalOverlay({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-background rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {children}
      </div>
    </div>
  )
}

// ─── Form components ─────────────────────────────────────────────

function EditItemForm({ item, categories, onClose, qc, showToast }: { item: StockItem; categories: StockCategory[]; onClose: () => void; qc: any; showToast: (t: 'success' | 'error', m: string) => void }) {
  const { currencyCode } = useCurrency()
  const [form, setForm] = useState({ name: item.name, unit: item.unit, category_id: item.category_id ?? '', reorder_level: item.reorder_level, default_unit_cost: item.default_unit_cost ?? 0, supplier_id: item.supplier_id ?? '', notes: item.notes ?? '' })
  const isWeight = UNIT_OPTIONS.weight.some(o => o.value === item.unit)
  const [unitMode, setUnitMode] = useState<UnitMode>(isWeight ? 'weight' : 'quantity')
  const { data: suppliersRes } = useQuery({ queryKey: ['suppliers'], queryFn: () => apiClient.listSuppliers() })
  const suppliers = suppliersRes?.data ?? []
  // Lane A (kitchen flow): the buying unit. Only rendered and sent with the
  // flag on; an empty name clears it on the server.
  const kitchenFlow = useKitchenFlowEnabled()
  const [purchaseUnit, setPurchaseUnit] = useState({ label: item.purchase_unit_label ?? '', factor: item.purchase_unit_factor ? String(item.purchase_unit_factor) : '' })
  const puLabel = purchaseUnit.label.trim()
  const puFactor = Number(purchaseUnit.factor)
  const puInvalid = kitchenFlow && Boolean(puLabel) !== (puFactor > 0)
  const [isReturnable, setIsReturnable] = useState(Boolean(item.is_returnable))
  // Expiry tracking (F-CTRL-N73). Switching it on changes nothing about the
  // stock already held; it makes the NEXT receipt ask for a date.
  const [tracksExpiry, setTracksExpiry] = useState(Boolean(item.tracks_expiry))
  const [shelfLife, setShelfLife] = useState(item.shelf_life_days ? String(item.shelf_life_days) : '')
  const shelf = parseShelfLife(shelfLife)
  // Tracking just switched on for an item that already holds stock: offer to
  // date that stock straight after saving.
  const [dateStockOpen, setDateStockOpen] = useState(false)
  const offerDating = tracksExpiry && !item.tracks_expiry && item.quantity_on_hand > 0
  const mut = useMutation({
    mutationFn: () => apiClient.updateStockItem(item.id, {
      tracks_expiry: tracksExpiry,
      // 0 clears the shelf life on the server.
      shelf_life_days: tracksExpiry ? (shelf.days ?? 0) : 0,
      name: form.name, unit: form.unit,
      category_id: form.category_id || undefined,
      reorder_level: form.reorder_level,
      default_unit_cost: form.default_unit_cost || undefined,
      // Send '' to explicitly clear the vendor; a UUID to set it.
      supplier_id: form.supplier_id,
      notes: form.notes || undefined,
      ...(kitchenFlow ? { purchase_unit_label: puLabel, ...(puLabel ? { purchase_unit_factor: puFactor } : {}), is_returnable: isReturnable } : {}),
    } as any),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stockItem', item.id] })
      qc.invalidateQueries({ queryKey: ['stockItems'] })
      qc.invalidateQueries({ queryKey: ['stockSummary'] })
      qc.invalidateQueries({ queryKey: ['advancedStockReport'] })
      qc.invalidateQueries({ queryKey: ['undatedLots'] })
      showToast('success', 'Item updated')
      if (offerDating) setDateStockOpen(true)
      else onClose()
    },
    onError: (err: Error) => showToast('error', err.message || 'Failed to update item'),
  })

  return (
    <form onSubmit={e => { e.preventDefault(); mut.mutate() }} className="p-6 space-y-4">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-base font-semibold">Edit: {item.name}</h2>
        <button type="button" onClick={onClose}><X className="w-4 h-4" /></button>
      </div>
      <FormField label="Name" required><input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required /></FormField>
      <div className="grid grid-cols-2 gap-4">
        <FormField label="Measured By">
          <UnitSelector unitMode={unitMode} unit={form.unit} onModeChange={mode => { setUnitMode(mode); setForm(f => ({ ...f, unit: UNIT_OPTIONS[mode][0].value })) }} onUnitChange={u => setForm(f => ({ ...f, unit: u }))} />
        </FormField>
        <FormField label="Category">
          <select value={form.category_id} onChange={e => setForm(f => ({ ...f, category_id: e.target.value }))}>
            <option value="">None</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </FormField>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <FormField label="Reorder Level"><input type="number" step="any" value={form.reorder_level} onChange={e => setForm(f => ({ ...f, reorder_level: +e.target.value }))} /></FormField>
        <FormField label={`Unit Cost (${currencyCode})`}><input type="number" step="0.01" value={form.default_unit_cost} onChange={e => setForm(f => ({ ...f, default_unit_cost: +e.target.value }))} /></FormField>
      </div>
      <FormField label="Vendor / supplier">
        <select value={form.supplier_id} onChange={e => setForm(f => ({ ...f, supplier_id: e.target.value }))}>
          <option value="">None</option>
          {suppliers.map(s => (
            <option key={s.id} value={s.id}>
              {s.name}{s.commission_percent ? ` (${s.commission_percent}% commission)` : ''}
            </option>
          ))}
        </select>
      </FormField>
      {kitchenFlow && (
        <>
          <div className="grid grid-cols-2 gap-4">
            <FormField label={IM.purchaseUnit.labelLabel}>
              <input value={purchaseUnit.label} placeholder={IM.purchaseUnit.labelPlaceholder} maxLength={40}
                onChange={e => setPurchaseUnit(p => ({ ...p, label: e.target.value }))} />
            </FormField>
            <FormField label={IM.purchaseUnit.factorLabel(form.unit)}>
              <input type="number" step="any" min="0" value={purchaseUnit.factor}
                onChange={e => setPurchaseUnit(p => ({ ...p, factor: e.target.value }))} />
            </FormField>
          </div>
          <p className="text-xs text-muted-foreground -mt-2">{IM.purchaseUnit.help}</p>
          <label className="flex min-h-10 cursor-pointer items-start gap-3 rounded-md border p-3">
            <input type="checkbox" className="mt-1 h-4 w-4" checked={isReturnable} onChange={e => setIsReturnable(e.target.checked)} />
            <span><span className="font-medium">{IM.returnable.label}</span><span className="block text-xs text-muted-foreground">{IM.returnable.help}</span></span>
          </label>
        </>
      )}
      <ExpiryTrackingFields tracks={tracksExpiry} shelfLife={shelfLife} onTracksChange={setTracksExpiry} onShelfLifeChange={setShelfLife} />
      {tracksExpiry && !item.tracks_expiry && item.quantity_on_hand > 0 && (
        <p className="-mt-2 text-xs text-muted-foreground">
          The {fmtQtyUnit(item.quantity_on_hand, item.unit)} already in stock keeps whatever dates it has. After saving you can date it lot by lot.
        </p>
      )}
      <FormField label="Notes"><textarea rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></FormField>
      <Button type="submit" className="w-full" disabled={mut.isPending || !form.name || puInvalid || (tracksExpiry && Boolean(shelf.error))}>{mut.isPending ? 'Saving...' : 'Save Changes'}</Button>
      <DateStockDialog open={dateStockOpen} onlyItemId={item.id} showToast={showToast}
        onClose={() => { setDateStockOpen(false); onClose() }} />
    </form>
  )
}

// ─── Redesigned Issue Form with unit conversion + reason ─────────

/** Unit-select value meaning "the item's buying unit" (Lane A). */
const PURCHASE_UNIT_OPTION = '__purchase_unit__'

function IssueForm({ item, users, onClose, qc, showToast }: { item: StockItem; users: UserBrief[]; onClose: () => void; qc: any; showToast: (t: 'success' | 'error', m: string) => void }) {
  const compatibleUnits = getCompatibleUnits(item.unit)
  // Lane A (kitchen flow): the item's buying unit ("Bag 25 kg") is one more
  // way to type the quantity — always sent to the server in the base unit —
  // and the batch to pick first is shown above the form.
  const kitchenFlow = useKitchenFlowEnabled()
  const pu = kitchenFlow ? purchaseUnitOf(item) : null
  const { data: lots } = useStockItemLots(item.id, kitchenFlow)
  const hasUnitOptions = compatibleUnits.length > 1 || pu != null

  const [form, setForm] = useState({
    quantity: 0,
    unit: item.unit,
    issued_to_user_id: '',
    reason: 'General Kitchen Use',
    note: '',
  })

  const inPurchaseUnits = pu != null && form.unit === PURCHASE_UNIT_OPTION
  const convertedQty = form.unit === item.unit
    ? form.quantity
    : inPurchaseUnits
      ? toBaseQty(form.quantity, pu.factor)
      : convertUnits(form.quantity, form.unit, item.unit)

  const conversionError = form.quantity > 0 && convertedQty === null
  // Flag on: the store's own shelf, as the server checks (Batch 5 lane stock).
  const shelf = storeShelf(item, lots, kitchenFlow)
  const insufficientStock = convertedQty != null && exceedsShelf(shelf, convertedQty)

  const issuedUser = users.find((u: UserBrief) => u.id === form.issued_to_user_id)
  const mut = useMutation({
    mutationFn: () => apiClient.issueStock(item.id, {
      quantity: inPurchaseUnits ? convertedQty ?? 0 : form.quantity,
      unit: form.unit !== item.unit && !inPurchaseUnits ? form.unit : undefined,
      issued_to_user_id: form.issued_to_user_id || undefined,
      reason: form.reason || undefined,
      note: form.note || undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stockItems'] })
      qc.invalidateQueries({ queryKey: ['stockAlerts'] })
      qc.invalidateQueries({ queryKey: ['stockMovements'] })
      qc.invalidateQueries({ queryKey: ['stockSummary'] })
      qc.invalidateQueries({ queryKey: ['advancedStockReport'] })
      showToast('success', `Issued ${form.quantity} ${inPurchaseUnits ? pu.label : form.unit} of ${item.name}${issuedUser ? ` to ${issuedUser.first_name}` : ''}`)
      onClose()
    },
    onError: (err: Error) => showToast('error', err.message || 'Failed to issue stock'),
  })

  const canSubmit = form.quantity > 0 && !conversionError && !insufficientStock && !mut.isPending

  return (
    <form onSubmit={e => { e.preventDefault(); mut.mutate() }} className="p-6 space-y-4">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-base font-semibold">Issue: {item.name}</h2>
        <button type="button" onClick={onClose}><X className="w-4 h-4" /></button>
      </div>

      {shelf.place == null ? (
        <div className="flex items-center gap-2 text-sm bg-muted/50 rounded-lg px-3 py-2">
          <Package className="w-4 h-4 text-muted-foreground" />
          <span className="text-muted-foreground">Available:</span>
          <span className="font-semibold">{item.quantity_on_hand} {item.unit}</span>
        </div>
      ) : (
        <div className="text-sm bg-muted/50 rounded-lg px-3 py-2 space-y-0.5">
          <div className="flex items-center gap-2">
            <Package className="w-4 h-4 text-muted-foreground" />
            <span className="font-semibold">
              {shelf.available != null ? ST.storeShelf.has(shelf.place, fmtQty(shelf.available), item.unit) : ST.storeShelf.checking(shelf.place)}
            </span>
          </div>
          <p className="text-xs text-muted-foreground">{ST.storeShelf.kitchenHint}</p>
        </div>
      )}
      {kitchenFlow && <FefoHint lots={lots} unit={item.unit} />}

      {/* Quantity + Unit side by side */}
      <div className={`grid gap-4 ${hasUnitOptions ? 'grid-cols-2' : 'grid-cols-1'}`}>
        <FormField label="Quantity" required>
          <input type="number" step="any" min="0.01" value={form.quantity || ''} onChange={e => setForm(f => ({ ...f, quantity: +e.target.value }))} required />
        </FormField>
        {hasUnitOptions && (
          <FormField label="Unit">
            <select value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}>
              {compatibleUnits.map(u => {
                const allUnits = [...UNIT_OPTIONS.weight, ...UNIT_OPTIONS.quantity]
                const found = allUnits.find(o => o.value === u)
                return <option key={u} value={u}>{found?.label ?? u}</option>
              })}
              {pu && <option value={PURCHASE_UNIT_OPTION}>{pu.label}</option>}
            </select>
          </FormField>
        )}
      </div>

      {/* Conversion preview */}
      {form.quantity > 0 && form.unit !== item.unit && (
        <div className={`text-sm rounded-lg px-3 py-2 ${conversionError ? 'bg-danger-surface text-danger-fg' : insufficientStock ? 'bg-warning-surface text-warning-fg' : 'bg-info-surface text-info-fg'}`}>
          {conversionError && 'Cannot convert between these units'}
          {!conversionError && convertedQty != null && (
            <>
              {form.quantity} {inPurchaseUnits ? pu.label : form.unit} = <strong>{convertedQty.toFixed(4)} {item.unit}</strong> will be deducted
              {insufficientStock && <span className="block mt-0.5 font-medium">{shelf.place != null ? ST.storeShelf.notMore(shelf.place, fmtQty(shelf.available ?? 0), item.unit) : `Exceeds available stock (${item.quantity_on_hand} ${item.unit})`}</span>}
            </>
          )}
        </div>
      )}

      {insufficientStock && form.unit === item.unit && form.quantity > 0 && (
        <div className="text-sm rounded-lg px-3 py-2 bg-warning-surface text-warning-fg">
          {shelf.place != null
            ? ST.storeShelf.notMore(shelf.place, fmtQty(shelf.available ?? 0), item.unit)
            : `Requested ${form.quantity} ${item.unit} exceeds available stock (${item.quantity_on_hand} ${item.unit})`}
        </div>
      )}

      <FormField label="Reason" required>
        <select value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} required>
          {ISSUE_REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
      </FormField>

      <FormField label="Used by (optional)">
        <select value={form.issued_to_user_id} onChange={e => setForm(f => ({ ...f, issued_to_user_id: e.target.value }))}>
          <option value="">Nobody in particular</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.role})</option>)}
        </select>
      </FormField>

      <FormField label="Note (optional)"><input value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} placeholder="e.g. For kitchen prep" /></FormField>

      <Button type="submit" className="w-full" disabled={!canSubmit}>
        {mut.isPending ? 'Issuing...' : `Issue ${form.quantity > 0 ? form.quantity + ' ' + (inPurchaseUnits ? pu.label : form.unit) : 'Stock'}`}
      </Button>
    </form>
  )
}

function ReturnToSupplierForm({ item, onClose, qc, showToast }: { item: StockItem; onClose: () => void; qc: any; showToast: (t: 'success' | 'error', m: string) => void }) {
  const { formatCurrency } = useCurrency()
  const { data: suppliersRes } = useQuery({ queryKey: ['suppliers'], queryFn: () => apiClient.listSuppliers() })
  const suppliers = suppliersRes?.data ?? []
  const [form, setForm] = useState({
    supplier_id: '',
    quantity: '',
    refund_type: 'supplier_credit' as 'cash' | 'bank' | 'supplier_credit' | 'no_refund',
    refund_amount: '',
    reason: '',
    pin: '',
  })

  const qty = Number(form.quantity)
  const refundAmount = form.refund_type === 'no_refund' ? 0 : Number(form.refund_amount || 0)
  const qtyValid = Number.isFinite(qty) && qty > 0
  const tooMuch = qtyValid && qty > item.quantity_on_hand + 1e-9
  const refundInvalid = form.refund_type !== 'no_refund' && (!Number.isFinite(refundAmount) || refundAmount < 0)

  const mut = useMutation({
    mutationFn: () =>
      apiClient.returnStockToSupplier(item.id, {
        supplier_id: form.supplier_id,
        quantity: qty,
        unit_cost: item.default_unit_cost ?? undefined,
        refund_amount: refundAmount,
        refund_type: form.refund_type,
        reason: form.reason.trim(),
        pin: form.pin.trim(),
      }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['stockItems'] })
      qc.invalidateQueries({ queryKey: ['stockAlerts'] })
      qc.invalidateQueries({ queryKey: ['stockMovements'] })
      qc.invalidateQueries({ queryKey: ['stockSummary'] })
      qc.invalidateQueries({ queryKey: ['advancedStockReport'] })
      showToast('success', res.message || 'Supplier return recorded')
      onClose()
    },
    onError: (err: Error) => showToast('error', err.message || 'Failed to record supplier return'),
  })

  const canSubmit =
    !!form.supplier_id &&
    qtyValid &&
    !tooMuch &&
    !refundInvalid &&
    form.reason.trim().length > 0 &&
    form.pin.length === 4 &&
    !mut.isPending

  return (
    <form onSubmit={e => { e.preventDefault(); if (canSubmit) mut.mutate() }} className="p-6 space-y-4">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h2 className="text-base font-semibold">Return to supplier</h2>
          <p className="text-sm text-muted-foreground">{item.name}</p>
        </div>
        <button type="button" onClick={onClose}><X className="w-4 h-4" /></button>
      </div>

      <div className="rounded-lg border bg-muted/40 p-3 text-sm">
        <div className="flex justify-between gap-3">
          <span className="text-muted-foreground">On hand</span>
          <span className="font-semibold">{item.quantity_on_hand} {item.unit}</span>
        </div>
        <div className="flex justify-between gap-3 mt-1">
          <span className="text-muted-foreground">Default unit cost</span>
          <span className="font-semibold">{item.default_unit_cost != null ? formatCurrency(item.default_unit_cost) : '—'}</span>
        </div>
      </div>

      <FormField label="Supplier" required>
        <select value={form.supplier_id} onChange={e => setForm(f => ({ ...f, supplier_id: e.target.value }))} required>
          <option value="">Select supplier...</option>
          {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </FormField>

      <FormField label={`Quantity to return (${item.unit})`} required>
        <input
          type="number"
          step="0.01"
          min="0"
          value={form.quantity}
          onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))}
          placeholder="0.00"
          required
        />
      </FormField>
      {tooMuch && (
        <p className="text-sm text-warning-fg bg-warning-surface rounded-md px-3 py-2">
          Cannot return more than on-hand stock ({item.quantity_on_hand} {item.unit}).
        </p>
      )}

      <div className="grid grid-cols-2 gap-3">
        <FormField label="Refund type" required>
          <select
            value={form.refund_type}
            onChange={e => {
              const next = e.target.value as typeof form.refund_type
              setForm(f => ({ ...f, refund_type: next, refund_amount: next === 'no_refund' ? '' : f.refund_amount }))
            }}
            required
          >
            <option value="supplier_credit">Supplier credit</option>
            <option value="cash">Cash refund</option>
            <option value="bank">Bank refund</option>
            <option value="no_refund">No refund</option>
          </select>
        </FormField>
        <FormField label="Refund amount">
          <input
            type="number"
            step="0.01"
            min="0"
            value={form.refund_amount}
            disabled={form.refund_type === 'no_refund'}
            onChange={e => setForm(f => ({ ...f, refund_amount: e.target.value }))}
            placeholder={form.refund_type === 'no_refund' ? 'N/A' : '0.00'}
          />
        </FormField>
      </div>

      <FormField label="Reason" required>
        <input
          value={form.reason}
          onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
          placeholder="e.g. Expired before use; supplier agreed to credit"
          required
        />
      </FormField>

      <FormField label="Admin / manager PIN" required>
        <input
          type="password"
          inputMode="numeric"
          maxLength={4}
          value={form.pin}
          onChange={e => setForm(f => ({ ...f, pin: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
          required
        />
      </FormField>

      <Button type="submit" className="w-full" disabled={!canSubmit}>
        {mut.isPending ? 'Recording...' : 'Record supplier return'}
      </Button>
    </form>
  )
}

type AdjustReason = typeof ADJUST_REASONS[number]['value']
type AdjustFormState = {
  reason: AdjustReason
  unit_cost: number
  note: string
}

function AdjustForm({ item, onClose, qc, showToast, onSwitchToCount }: { item: StockItem; onClose: () => void; qc: any; showToast: (t: 'success' | 'error', m: string) => void; onSwitchToCount?: () => void }) {
  const { formatCurrency, currencyCode } = useCurrency()
  const [qtyStr, setQtyStr] = useState('')
  const [form, setForm] = useState<AdjustFormState>({
    reason: ADJUST_REASONS[0].value,
    unit_cost: item.default_unit_cost ?? 0,
    note: '',
  })

  const delta = qtyStr.trim() === '' ? NaN : Number(qtyStr)
  const deltaValid = Number.isFinite(delta) && Math.abs(delta) > 1e-9
  // Flag on: an adjustment changes the store's shelf only (Batch 5 lane stock).
  const kitchenFlow = useKitchenFlowEnabled()
  const { data: lots } = useStockItemLots(item.id, kitchenFlow)
  const shelf = storeShelf(item, lots, kitchenFlow)
  const tooMuchRemoval = deltaValid && delta < 0 && shelf.available != null && Math.abs(delta) > shelf.available + 1e-9
  // Adding stock creates a lot, and a lot of a tracked item needs its date
  // (F-CTRL-N73). Taking stock away never asks.
  const [typedExpiry, setTypedExpiry] = useState<string | null>(null)
  const today = businessTodayIso(useDayBoundaryHour())
  const expiry = typedExpiry ?? defaultExpiryFor(item, today)
  const needsExpiry = delta > 0 && expiryMissing(item, expiry)

  const mut = useMutation({
    mutationFn: () =>
      apiClient.adjustStock(item.id, {
        quantity_delta: delta,
        reason: form.reason || undefined,
        note: form.note.trim() || undefined,
        ...(delta > 0 && form.unit_cost > 0 ? { unit_cost: form.unit_cost } : {}),
        ...(delta > 0 && expiry ? { expiry_date: expiry } : {}),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stockItems'] })
      qc.invalidateQueries({ queryKey: ['stockItem'] })
      qc.invalidateQueries({ queryKey: ['stockItemLots'] })
      qc.invalidateQueries({ queryKey: ['expiringBatches'] })
      qc.invalidateQueries({ queryKey: ['stockAlerts'] })
      qc.invalidateQueries({ queryKey: ['stockMovements'] })
      qc.invalidateQueries({ queryKey: ['stockSummary'] })
      qc.invalidateQueries({ queryKey: ['advancedStockReport'] })
      showToast('success', `Adjusted ${item.name} by ${delta > 0 ? '+' : ''}${delta} ${item.unit}`)
      onClose()
    },
    onError: (err: Error) => showToast('error', err.message || 'Failed to adjust stock'),
  })

  const canSubmit = deltaValid && !tooMuchRemoval && !needsExpiry && !mut.isPending
  const qtyParseError = qtyStr.trim() !== '' && !Number.isFinite(delta)

  return (
    <form onSubmit={e => { e.preventDefault(); if (!deltaValid || tooMuchRemoval || needsExpiry) return; mut.mutate() }} className="p-6 space-y-4">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-base font-semibold">Adjust: {item.name}</h2>
        <button type="button" onClick={onClose}><X className="w-4 h-4" /></button>
      </div>
      <p className="text-sm text-muted-foreground">
        {shelf.place == null ? (
          <>Current on hand: <span className="font-medium text-foreground">{item.quantity_on_hand} {item.unit}</span>.</>
        ) : (
          <span className="font-medium text-foreground">
            {shelf.available != null ? ST.storeShelf.has(shelf.place, fmtQty(shelf.available), item.unit) : ST.storeShelf.checking(shelf.place)}.
          </span>
        )}
        {' '}Enter a positive number to add stock, or a negative number to remove it. When you remove stock, older receipts are used first so costs stay fair.
      </p>
      {shelf.place != null && <p className="text-xs text-muted-foreground">{ST.storeShelf.kitchenHint}</p>}
      <FormField label={`Quantity change (${item.unit})`} required>
        <input
          type="text"
          inputMode="decimal"
          value={qtyStr}
          onChange={e => setQtyStr(e.target.value)}
          placeholder="e.g. 2 or -1.5"
          required
        />
      </FormField>
      {qtyParseError && (
        <p className="text-sm text-red-600">Enter a valid number.</p>
      )}
      {tooMuchRemoval && (
        (shelf.available ?? 0) < 0 ? (
          <div className="text-sm text-danger-fg bg-danger-surface/60 rounded-md px-3 py-2 space-y-2">
            <p>On hand is negative — an adjustment can&apos;t fix that honestly. Count what&apos;s really on the shelf and the system sets it right in one step.</p>
            {onSwitchToCount && (
              <Button type="button" size="sm" variant="outline" className="h-8" onClick={onSwitchToCount}>
                <ClipboardCheck className="w-3.5 h-3.5 mr-1.5" /> Count instead
              </Button>
            )}
          </div>
        ) : (
          <p className="text-sm text-warning-fg bg-warning-surface rounded-md px-3 py-2">
            {shelf.place != null
              ? ST.storeShelf.notMore(shelf.place, fmtQty(shelf.available ?? 0), item.unit)
              : `Cannot remove more than on hand (${fmtQtyUnit(item.quantity_on_hand, item.unit)}).`}
          </p>
        )
      )}
      {delta > 0 && (
        <FormField label={`Unit cost for new lot (${currencyCode}, optional)`}>
          <input type="number" step="0.01" min="0" value={form.unit_cost || ''} onChange={e => setForm(f => ({ ...f, unit_cost: +e.target.value }))} />
        </FormField>
      )}
      {delta > 0 && form.unit_cost > 0 && (
        <p className="text-xs text-muted-foreground">Lot value: {formatCurrency(delta * form.unit_cost)}</p>
      )}
      {delta > 0 && (
        <ExpiryDateField id="adjust-expiry" item={item} value={expiry} onChange={setTypedExpiry} prefilled={typedExpiry === null} />
      )}
      <FormField label="Reason" required>
        <select value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value as AdjustReason }))} required>
          {ADJUST_REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
      </FormField>
      <FormField label="Note (optional)">
        <input value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} placeholder="e.g. Year-end count" />
      </FormField>
      <Button type="submit" className="w-full" disabled={!canSubmit}>
        {mut.isPending ? 'Saving...' : 'Apply adjustment'}
      </Button>
    </form>
  )
}

/**
 * Physical count, confirm-then-apply (migration 103): the count is
 * authoritative — on confirm the system updates the stock to what was counted
 * and files the difference as shrinkage/overage. The preview states exactly
 * what will change (including the money) before anything moves, with a louder
 * confirmation when the write-off is large. If stock moves while the dialog is
 * open, the backend 409s and the preview re-renders against the fresh number
 * instead of applying a correction the user never saw.
 */
function RecordCountForm({ item, onClose, qc, showToast }: { item: StockItem; onClose: () => void; qc: any; showToast: (t: 'success' | 'error', m: string) => void }) {
  const { formatCurrency } = useCurrency()
  const [countedStr, setCountedStr] = useState('')
  const [note, setNote] = useState('')
  const [confirming, setConfirming] = useState(false)
  // The book quantity the preview is computed against. Starts from the row we
  // opened with; refreshed from the backend's 409 payload if stock moves.
  const [bookQty, setBookQty] = useState(item.quantity_on_hand)

  // Lane A (kitchen flow): count by scanning. Each scan of this item's code
  // adds one pack to the counted figure (the first replaces what was typed);
  // another item's code is refused rather than counted here.
  const kitchenFlow = useKitchenFlowEnabled()
  const [scannedOnce, setScannedOnce] = useState(false)
  const scanner = useItemScanner({
    enabled: kitchenFlow && !confirming,
    onItem: (match) => {
      if (match.item.id !== item.id) return { ok: false, text: IM.scan.wrongItem(match.item.name, item.name) }
      const next = qtyAfterScan(Number(countedStr) || 0, match.pack_qty, !scannedOnce)
      setCountedStr(String(next))
      setScannedOnce(true)
      return { ok: true, text: IM.scan.added(item.name, fmtQty(next), item.unit) }
    },
  })

  // Kitchen flow Stage 3: count one place, previewed against its balance.
  const countAt = useCountLocation(kitchenFlow, item.id)
  useEffect(() => {
    if (countAt.book != null) setBookQty(countAt.book)
  }, [countAt.locationId, countAt.book])

  const unitCost = item.wac_unit_cost ?? item.default_unit_cost ?? 0
  const counted = countedStr.trim() === '' ? NaN : Number(countedStr)
  const countedValid = Number.isFinite(counted) && counted >= 0
  const parseError = countedStr.trim() !== '' && (!Number.isFinite(counted) || counted < 0)
  const variance = countedValid ? counted - bookQty : NaN
  const hasVariance = countedValid && Math.abs(variance) > 1e-9
  const varianceValue = hasVariance ? variance * unitCost : 0
  // Loud confirmation for a big write-off: over Rs 5,000, or over a quarter of
  // the item's book value.
  const bookValue = bookQty * unitCost
  const bigVariance = hasVariance && unitCost > 0 &&
    (Math.abs(varianceValue) > 5000 || (bookValue > 0 && Math.abs(varianceValue) > 0.25 * bookValue))

  const mut = useMutation({
    mutationFn: () =>
      kitchenFlow && countAt.locationId
        ? apiClient.recordStockCountAt(item.id, {
          counted_quantity: counted,
          note: note.trim() || undefined,
          expected_book_quantity: bookQty,
          location_id: countAt.locationId,
        })
        : apiClient.recordStockCount(item.id, {
          counted_quantity: counted,
          note: note.trim() || undefined,
          expected_book_quantity: bookQty,
        }),
    onSuccess: (res) => {
      // The count moved stock — refresh everything that shows a quantity.
      qc.invalidateQueries({ queryKey: ['stockItems'] })
      qc.invalidateQueries({ queryKey: ['stockSummary'] })
      qc.invalidateQueries({ queryKey: ['stockAlerts'] })
      qc.invalidateQueries({ queryKey: ['stockMovements'] })
      qc.invalidateQueries({ queryKey: ['advancedStockReport'] })
      qc.invalidateQueries({ queryKey: ['inventoryActivity'] })
      const v = res.data?.variance ?? (counted - bookQty)
      showToast('success', Math.abs(v) <= 1e-9
        ? `Counted ${item.name}: ${counted} ${item.unit} — matched the system exactly.`
        : `Counted ${item.name}: stock set to ${counted} ${item.unit} (difference ${v > 0 ? '+' : ''}${+v.toFixed(4)} ${item.unit} recorded).`)
      onClose()
    },
    onError: (err: Error) => {
      const apiErr = err as ApiClientError
      if (apiErr.code === 'book_quantity_changed') {
        const fresh = (apiErr.payload as { book_quantity?: number } | undefined)?.book_quantity
        if (typeof fresh === 'number') setBookQty(fresh)
        setConfirming(false)
        showToast('error', 'Stock moved while you were counting — review the updated difference and confirm again.')
        return
      }
      showToast('error', err.message || 'Failed to record count')
    },
  })

  const canProceed = countedValid && !mut.isPending && !countAt.loading

  return (
    <form
      onSubmit={e => {
        e.preventDefault()
        if (!countedValid) return
        // No difference → nothing to confirm; apply directly.
        if (!confirming && hasVariance) { setConfirming(true); return }
        mut.mutate()
      }}
      className="p-6 space-y-4"
    >
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-base font-semibold">Count: {item.name}</h2>
        <button type="button" onClick={onClose}><X className="w-4 h-4" /></button>
      </div>
      {scanner.ui}
      {!confirming && countAt.ui}

      {!confirming ? (
        <>
          <p className="text-sm text-muted-foreground">
            The system currently expects <span className="font-medium text-foreground tabular-nums">{bookQty} {item.unit}</span>.
            Count what is physically on the shelf and enter it — the system updates the stock to your count
            and records any difference.
          </p>
          <FormField label={`You counted (${item.unit})`} required>
            <input
              type="text"
              inputMode="decimal"
              value={countedStr}
              onChange={e => setCountedStr(e.target.value)}
              placeholder="e.g. 12 or 4.5"
              autoFocus
              required
            />
          </FormField>
          {parseError && (
            <p className="text-sm text-red-600">Enter a valid non-negative number.</p>
          )}
          {hasVariance && (
            <p className={`text-sm rounded-md px-3 py-2 ${variance < 0 ? 'bg-danger-surface text-danger-fg' : 'bg-warning-surface text-warning-fg'}`}>
              {variance < 0 ? 'That is ' : 'That is '}
              <span className="font-semibold tabular-nums">{Math.abs(+variance.toFixed(4))} {item.unit} {variance < 0 ? 'less' : 'more'}</span>
              {' '}than the system expects
              {unitCost > 0 ? <> — about <span className="font-semibold">{formatCurrency(Math.abs(varianceValue))}</span> {variance < 0 ? 'missing' : 'extra'}</> : null}.
            </p>
          )}
          {countedValid && !hasVariance && (
            <p className="text-sm rounded-md px-3 py-2 bg-success-surface text-success-fg">
              Matches the system exactly.
            </p>
          )}
          <FormField label="Note (optional)">
            <input value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. Monthly stock-take" />
          </FormField>
          <Button type="submit" className="w-full" disabled={!canProceed}>
            {mut.isPending ? 'Saving…' : hasVariance ? 'Review & apply' : 'Record count'}
          </Button>
        </>
      ) : (
        <>
          <div className={`rounded-md border px-4 py-3 text-sm space-y-1.5 ${bigVariance ? 'border-danger-border bg-danger-surface/40' : 'border-warning-border bg-warning-surface/30'}`}>
            <p>
              You counted <span className="font-semibold tabular-nums">{counted} {item.unit}</span>;
              the system expected <span className="font-semibold tabular-nums">{bookQty} {item.unit}</span>.
            </p>
            <p>
              Applying will set the stock to <span className="font-semibold tabular-nums">{counted} {item.unit}</span> and record
              {' '}<span className="font-semibold tabular-nums">{Math.abs(+variance.toFixed(4))} {item.unit}</span> as {variance < 0 ? 'missing' : 'found'}
              {unitCost > 0 ? <> — <span className={`font-semibold ${variance < 0 ? 'text-red-600' : ''}`}>{formatCurrency(Math.abs(varianceValue))}</span> {variance < 0 ? 'written off' : 'added'}</> : null}.
            </p>
            {bigVariance && (
              <p className="font-medium text-danger-fg">
                That is a large difference. Please double-check the shelf and the unit ({item.unit}) before applying.
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" className="flex-1" disabled={mut.isPending} onClick={() => setConfirming(false)}>
              Back
            </Button>
            <Button type="submit" variant={bigVariance ? 'destructive' : 'default'} className="flex-1" disabled={mut.isPending}>
              {mut.isPending ? 'Applying…' : 'Apply count'}
            </Button>
          </div>
        </>
      )}
    </form>
  )
}

